
import React from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import Search from "./pages/Search";
import Profile from "./pages/Profile";
import Shop from "./pages/Shop";
import CollectionPage from "./pages/CollectionPage";
import ProductDetail from "./pages/ProductDetail";
import Wishlist from "./pages/Wishlist";
import NotFound from "./pages/NotFound";
import MyList from "./pages/MyList";
import Playlists from "./pages/Playlists";
import VideoDetail from "./pages/VideoDetail";
import Browse from "./pages/Browse";
import StoryDetail from "./pages/StoryDetail";
import AdminPanel from "./pages/Admin/AdminPanel";
import StoryEditor from "./pages/Admin/StoryEditor";
import { ListProvider } from "./contexts/ListContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import HotelsHomepage from "./pages/Hotels/HotelsHomepage";
import HotelSearch from "./pages/Hotels/HotelSearch";
import HotelDetail from "./pages/Hotels/HotelDetail";
import VideoLibrary from "./pages/VideoLibrary";
import ScrollToTop from "./components/utils/ScrollToTop";
import Stories from "./pages/Stories";
import AboutPage from "./pages/info/AboutPage";
import ContactPage from "./pages/info/ContactPage";
import PrivacyPolicyPage from "./pages/info/PrivacyPolicyPage";
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import ForgotPassword from './pages/auth/ForgotPassword';
import PasswordReset from './pages/auth/PasswordReset';
import EmailConfirmation from './pages/auth/EmailConfirmation';

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ThemeProvider>
        <AuthProvider>
          <ListProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/search" element={<Search />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/collection/:handle" element={<CollectionPage />} />
                <Route path="/product/:handle" element={<ProductDetail />} />
                <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
                
                {/* Protected Routes */}
                <Route path="/my-list" element={<MyList />} />
                <Route path="/playlists" element={<Playlists />} />
                
                <Route path="/videos/:slug" element={<VideoDetail />} />
                <Route path="/browse" element={<Browse />} />
                <Route path="/stories" element={<Stories />} />
                <Route path="/stories/:slug" element={<StoryDetail />} />
                
                {/* Admin Routes - Protected */}
            <Route path="/admin" element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            } />
            <Route path="/admin/stories/new" element={
              <AdminRoute>
                <StoryEditor />
              </AdminRoute>
            } />
            <Route path="/admin/stories/edit/:id" element={
              <AdminRoute>
                <StoryEditor />
              </AdminRoute>
            } />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/password-reset" element={<PasswordReset />} />
            <Route path="/auth/confirm" element={<EmailConfirmation />} />
                
                <Route path="/videos" element={<VideoLibrary />} />
                <Route path="/hotels" element={<HotelsHomepage />} />
                <Route path="/hotels/search" element={<HotelSearch />} />
                <Route path="/hotels/:id" element={<HotelDetail />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/faq" element={<NotFound />} />
                <Route path="/privacy-choices" element={<NotFound />} />
                <Route path="/advertise" element={<NotFound />} />
                <Route path="/press-releases" element={<NotFound />} />
                <Route path="/licensing" element={<NotFound />} />
                <Route path="/inquiries" element={<NotFound />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </ListProvider>
        </AuthProvider>
      </ThemeProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
